// ListViewItem.jsx

import React, { useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';

// Utilities
import { resolveNodeIcon } from 'utilities/resolveNodeIcon';

// Hooks
import useListViewItemCoverState from './hooks/useListViewItemCoverState';

// CSS
import './ListViewItem.css';

// Icons
import Icon from 'svg/Icon';

// Components
import ListViewItemFieldRow from './ListViewItemFieldRow';
import Pill from 'components/pill/Pill';

/**
 * ListViewItem
 *
 * Displays a single node in list format.
 * 
 * @param {object} node - The node to display.
 * @param {string} coverUrl - Optional cover image URL for the node.
 * @param {string} subtitle - Optional subtitle text for the node.
 * @param {array} enumPills - Optional array of enum pills to display.
 * @param {array} fieldValues - Optional array of field values to display.
 * @param {ReactNode|null} topRightAction - Optional action rendered at the top-right of the item.
 * @param {function} onClick - Click handler for the item.
 * @param {function} onDoubleClick - Double-click handler for the item.
 *
 * @returns {ReactNode} The rendered list view item.
 */
const ListViewItem = ({
  node,
  coverUrl = '',
  subtitle = '',
  enumPills = [],
  fieldValues = [],
  topRightAction = null,
  onClick,
  onDoubleClick,
  draggable = false,
  onDragStart = null,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // Node Types Context
  const { getType } = useNodeTypes();

  // ----- STATE ----- //

  // Hover state

  const [hovered, setHovered] = useState(false);

  const title = node?.title || 'Untitled';
  const safeSubtitle = typeof subtitle === 'string' ? subtitle.trim() : '';
  const hasEnumPills = Array.isArray(enumPills) && enumPills.length > 0;
  const hasFields = Array.isArray(fieldValues) && fieldValues.length > 0;
  const nodeType = getType(node?.type);
  const {
    iconKey: resolvedIconKey,
    iconPath: resolvedIconPath,
    iconResource: resolvedIconResource,
  } = resolveNodeIcon({
    node,
    typeDef: nodeType,
  });
  const hasTopRightAction = Boolean(topRightAction);
  const fallbackIconKey = resolvedIconKey || (
    !resolvedIconPath && !resolvedIconResource ? 'deployed_code' : ''
  );

  // ----- HOOKS ----- //

  const {
    coverContainerRef,
    displayCoverUrl,
    handleCoverError,
    handleCoverLoad,
    showCover,
    showCoverShimmer,
  } = useListViewItemCoverState({
    coverUrl,
    node,
  });

  if (!node) return null;

  const identityColor = typeof node.color === 'string' ? node.color.trim() : '';

  // ----- RENDER ----- //

  return (
    <div
      className="list-view-item-container"
      style={{
        backgroundColor: hovered ? theme.backgroundHover : theme.backgroundMuted,
        ...(identityColor ? { borderLeft: `3px solid ${identityColor}` } : null),
      }}
      draggable={draggable}
      onDragStart={draggable ? onDragStart : undefined}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {showCover ? (
        <div
          className="list-view-item-cover"
          ref={coverContainerRef}
          aria-hidden="true"
        >

          {/* COVER SHIMMER */}
          {showCoverShimmer && <div className="list-view-item-cover-shimmer" />}

          {/* COVER IMAGE */}
          {displayCoverUrl && (
            <img
              className="list-view-item-cover-image"
              style={{
                opacity: showCoverShimmer ? 0 : 1,
              }}
              alt=""
              src={displayCoverUrl}
              draggable={false}
              onError={handleCoverError}
              onLoad={handleCoverLoad}
            />
          )}
        </div>
      ) : resolvedIconResource ? (
        <img
          alt=""
          className="list-view-item-icon-resource"
          draggable={false}
          src={resolvedIconResource}
          width={14}
          height={14}
        />
      ) : fallbackIconKey ? (
        <Icon
          iconKey={fallbackIconKey}
          width={14}
          height={14}
          color={theme.foregroundMuted}
        />
      ) : (
        <Icon
          path={resolvedIconPath}
          width={14}
          height={14}
          color={theme.foregroundMuted}
        />
      )}

      {/* CONTENT */}
      <div className="list-view-item-content">

        {/* ACTION */}
        {hasTopRightAction && (
          <div
            className="list-view-item-action"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onDoubleClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {topRightAction}
          </div>
        )}

        {/* LINES */}
        <div className="list-view-item-line">

          {/* TITLE */}
          <div className="list-view-item-title">
            {title}
          </div>

          {/* PILLS */}
          {hasEnumPills && (

            <div className="list-view-item-pills">
              {enumPills.map((pill) => (
                <Pill
                  className="list-view-item-pill"
                  key={pill.key}
                  color={pill.color || theme.foregroundMuted}
                  compact
                  iconKey={pill.iconKey}
                  label={pill.label}
                  labelColor={theme.highlightForeground}
                />
              ))}
            </div>
          )}

          {/* DESCRIPTION */}
          {safeSubtitle && (
            <div
              className="list-view-item-description"
            >
              {safeSubtitle}
            </div>
          )}

          {/* FIELDS */}
          {hasFields && (
            <div
              className="list-view-item-fields"
            >
              {fieldValues.map((value, index) => {
                return (
                  <ListViewItemFieldRow
                    key={`list_field_${node?.key}_${index}`}
                    fieldValue={value}
                    index={index}
                    nodeKey={node?.key || ''}
                    theme={theme}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Dev Tools
ListViewItem.displayName = 'ListViewItem';

export default ListViewItem;
